import type { DatasetEntity } from '../types/dataImportExport.js';

/** Client workbook format version; not a server schema/version negotiation. */
export const PARTY_TEMPLATE_VERSION = 1;

export const PARTY_FIELD_LABELS: Readonly<Record<string, string>> = {
  code: 'کد طرف‌حساب',
  name: 'نام طرف‌حساب',
  kind: 'نوع طرف‌حساب',
  status: 'وضعیت',
  nationalId: 'شناسه ملی / کد ملی',
  economicCode: 'کد اقتصادی',
  postalCode: 'کد پستی',
  address: 'آدرس',
  mobile: 'موبایل',
  phone: 'تلفن',
  email: 'ایمیل',
  role: 'نقش',
  roles: 'نقش‌ها',
};

const FIELD_GUIDE: Readonly<Record<string, readonly [string, string]>> = {
  code: ['کد یکتای طرف‌حساب؛ صفر ابتدای کد را حفظ کنید.', 'P001'],
  name: ['نام نمایشی طرف‌حساب؛ شباهت نام به‌تنهایی باعث ادغام نمی‌شود.', 'نام طرف‌حساب'],
  kind: ['فقط person (شخص) یا organization (سازمان).', 'organization'],
  status: ['فقط active یا inactive؛ خالی یعنی active.', 'active'],
  nationalId: [
    'برای person: کد ملی ۱۰ رقمی معتبر؛ برای organization: شناسه حقوقی ۱۱ رقمی معتبر، با checksum.',
    '۱۰ یا ۱۱ رقم معتبر، به‌صورت متن',
  ],
  economicCode: [
    'اختیاری؛ برای organization، شناسه ملی معتبر ۱۱رقمی شرکت یا کد اقتصادی قدیمی ۱۲رقمی؛ برای person، کد اقتصادی ۱۲رقمی. شناسه ۱۱رقمی باید با شناسه ملی واردشده همان شرکت یکسان باشد. مقدار را متن وارد کنید یا خالی بگذارید.',
    'شناسه ملی معتبر ۱۱رقمی شرکت یا کد اقتصادی ۱۲رقمی',
  ],
  postalCode: ['کد پستی به‌صورت متن؛ صفر ابتدای آن را حفظ کنید.', 'کد پستی'],
  address: ['آدرس اصلی؛ این قالب تماس‌ها یا آدرس‌های چندردیفی را وارد نمی‌کند.', 'آدرس اصلی'],
  mobile: ['موبایل ایرانی؛ 09، 9، +98 و 0098 با فاصله یا خط تیره پذیرفته می‌شوند.', '09121234567'],
  phone: ['شماره تلفن اصلی به‌صورت متن؛ شماره مشترک باعث ادغام نمی‌شود.', '02112345678'],
  email: ['ایمیل اصلی به‌صورت متن.', 'contact@example.com'],
  role: ['فقط customer، supplier یا both؛ هم‌زمان با roles مقدار ندهید.', 'customer'],
  roles: [
    'customer یا supplier یا both؛ چند نقش با کاما یا |؛ هم‌زمان با role مقدار ندهید.',
    'customer|supplier',
  ],
};

/** Columns come from the current API registry, never an assumed Party V2 rollout. */
export async function buildPartyTemplateBlob(entity: DatasetEntity): Promise<Blob> {
  if (entity.entityClass !== 'party' || entity.availability !== 'ready' || !entity.permitted) {
    throw new Error('قالب طرف‌حساب فقط برای موجودیت آماده و مجاز قابل دریافت است.');
  }
  const fields = [...new Set(entity.writableFields)];
  if (fields.length === 0 || entity.requiredFields.some((field) => !fields.includes(field))) {
    throw new Error('تعریف فیلدهای قالب طرف‌حساب ناقص است.');
  }

  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const data = workbook.addWorksheet('DATA', {
    views: [{ rightToLeft: true, state: 'frozen', ySplit: 1 }],
  });
  data.columns = fields.map((field) => ({
    header: PARTY_FIELD_LABELS[field] ?? field,
    key: field,
    width: field === 'address' ? 42 : 26,
    // Formatting the whole column preserves leading zeros when the user types values.
    style: { numFmt: '@' },
  }));
  data.getRow(1).font = { bold: true };

  const guide = workbook.addWorksheet('راهنما', { views: [{ rightToLeft: true }] });
  guide.addRow(['قالب ورود طرف‌حساب', `نسخه قالب رابط کاربری: ${PARTY_TEMPLATE_VERSION}`]);
  guide.addRow(['ورود داده', 'فقط شیت اول DATA وارد می‌شود؛ داده‌های خود را از ردیف ۲ وارد کنید.']);
  guide.addRow([
    'نگاشت صریح',
    'هدر فارسی خودکار نگاشت نمی‌شود؛ در /data هر ستون را به فیلد مقصد انتخاب کنید.',
  ]);
  guide.addRow([
    'نمونه‌ها',
    'نمونه‌های این راهنما فقط توضیح هستند؛ آن‌ها را به شیت DATA کپی نکنید.',
  ]);
  guide.addRow([
    'نوع داده',
    'همه ستون‌ها متن هستند؛ شناسه و شماره را به عدد یا scientific notation تبدیل نکنید.',
  ]);
  guide.addRow([
    'فیلد اختیاری',
    'برای ثبت اولیه کد، نام و نوع طرف‌حساب را وارد کنید. اطلاعات اختیاری مثل شناسه ملی، کد اقتصادی و تماس را می‌توانید خالی بگذارید و بعداً در فرم ویرایش تکمیل کنید؛ مقدار واردشده باید معتبر باشد و فیلد بدون مجوز رد می‌شود.',
  ]);
  guide.addRow([
    'تطبیق',
    'پیش از ثبت نتیجه بررسی را ببینید؛ شباهت نام و تلفن ادغام خودکار نیست. برای تکمیل مخاطب موجود از فرم ویرایش استفاده کنید؛ بارگذاری مجدد اطلاعاتش را تکمیل نمی‌کند.',
  ]);
  guide.addRow(['نسخه', 'این عدد نسخه قالب کلاینت است، نه نسخه رسمی یا تضمین مجوز فیلدهای سرور.']);
  guide.addRow([]);
  guide.addRow(['هدر فایل', 'فیلد مقصد', 'الزام', 'نوع', 'قاعده / مقدار مجاز', 'مثال توضیحی']);
  for (const field of fields) {
    const [description, example] = FIELD_GUIDE[field] ?? ['طبق اعتبارسنجی سرور.', ''];
    guide.addRow([
      PARTY_FIELD_LABELS[field] ?? field,
      field,
      entity.requiredFields.includes(field) ? 'اجباری' : 'اختیاری',
      'متن',
      description,
      example,
    ]);
  }
  guide.columns = [
    { width: 28 },
    { width: 34 },
    { width: 14 },
    { width: 12 },
    { width: 85 },
    { width: 34 },
  ];
  guide.eachRow((row) => {
    row.alignment = { vertical: 'top', wrapText: true };
    row.eachCell((cell) => {
      cell.numFmt = '@';
    });
  });
  guide.getRow(1).font = { bold: true };
  guide.getRow(10).font = { bold: true };
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
